import type Tracker from "@openreplay/tracker";
import { string } from "yup";
import { useFormStore } from "@/store/form";
import { useProductStore } from "@/store/product";
import { getOnlyNumbers } from "@/utils";
import { validateDDD, validateTel } from "@/utils/validators/tel";

const emailSchema = string().email().required();
const textValue = (value: unknown): string => typeof value === "string" ? value.trim() : "";

function getCheckoutMetadata() {
  const { value, saleId } = useFormStore.getState();
  const product = useProductStore.getState();
  const emailValue = textValue(value.email);
  const phoneDigits = getOnlyNumbers(textValue(value.tel));
  const msisdn = phoneDigits.length === 11 && validateTel(phoneDigits) ? phoneDigits : "";
  const offerDDD = textValue(product.areaCode);
  const validOfferDDD = offerDDD.length === 2 && validateDDD(offerDDD) ? offerDDD : "";

  return {
    email: emailSchema.isValidSync(emailValue) ? emailValue : "",
    msisdn,
    venda_id: saleId === null ? "" : String(saleId),
    oferta_slug: textValue(value.ofertaSlug),
    tipo_venda: textValue(value.tipo),
    segmento: textValue(product.item?.segmento),
    forma_pagamento: textValue(product.payment?.value ?? value.paymentType?.value),
    ddd: msisdn ? msisdn.slice(0, 2) : validOfferDDD,
    utm_source: textValue(value.utm_source),
    utm_campaign: textValue(value.utm_campaign),
  };
}

export function syncCheckoutContext(tracker: Pick<Tracker, "identify" | "setMetadata">) {
  const lastMetadata = new Map<string, string>();
  let lastEmail = "";

  function sync() {
    try {
      const metadata = getCheckoutMetadata();
      // O telefone é pesquisável como metadado; somente email identifica o usuário.
      if (metadata.email !== lastEmail) {
        tracker.identify(metadata.email);
        lastEmail = metadata.email;
      }

      for (const [key, value] of Object.entries(metadata)) {
        if (value === (lastMetadata.get(key) ?? "")) continue;
        tracker.setMetadata(key, value);
        lastMetadata.set(key, value);
      }
    } catch {
      // A observabilidade não pode interromper uma atualização do checkout.
      console.warn("[OpenReplay] Não foi possível atualizar o contexto.");
    }
  }

  // Inclui dados restaurados e alterações ocorridas durante a inicialização do SDK.
  sync();
  const unsubscribeForm = useFormStore.subscribe(sync);
  const unsubscribeProduct = useProductStore.subscribe(sync);
  return () => {
    unsubscribeForm();
    unsubscribeProduct();
  };
}
